import { useContext } from 'react';
import { X } from 'lucide-react';

import { Menu } from '@components/primitives/Menu';
import { cn } from '@components/primitives/utilities';

import { EQLFilterValuesContext } from './context';
import { EQLFilterValueSelect } from './EQLFilterValueSelect';
import { EQLFilterChipConfig } from './types';

import css from './styles.module.css';

export interface EQLFilterChipsProps {
  config: EQLFilterChipConfig[];
  onChange?: (id: string, next: EQLFilterChipConfig) => void;
  onRemove?: (id: string) => void;
  measure?: boolean;
}

export function EQLFilterChips({
  config,
  onChange = () => {},
  onRemove = () => {},
  measure = false,
}: EQLFilterChipsProps) {
  return (
    <>
      {config.map((chip) => (
        <EQLFilterChip
          key={chip.id}
          chip={chip}
          autoOpen={!measure && !chip.value}
          onChange={(next) => onChange(chip.id, next)}
          onRemove={() => onRemove(chip.id)}
        />
      ))}
    </>
  );
}

function EQLFilterChip({
  chip,
  autoOpen,
  onChange,
  onRemove,
}: {
  chip: EQLFilterChipConfig;
  autoOpen: boolean;
  onChange: (next: EQLFilterChipConfig) => void;
  onRemove: () => void;
}) {
  const useValues = useContext(EQLFilterValuesContext);
  const valueOptions = chip.field.items?.length
    ? chip.field.items.map((item) => item.value)
    : chip.operator.suggestions;
  const hasValueList = Boolean(
    valueOptions?.length || (chip.field.fetchValues && useValues)
  );

  return (
    <div className={css.chip} tabIndex={0}>
      <span className={cn(css.segment, css.field)} title={chip.field.name}>
        {chip.field.name}
      </span>

      <Menu>
        <Menu.Trigger asChild>
          <button className={cn(css.segment, css.operatorButton)}>
            {chip.operator.label}
          </button>
        </Menu.Trigger>
        <Menu.List align="start">
          {chip.field.operators.map((op) => (
            <Menu.Item
              key={op.name}
              onSelect={() => onChange({ ...chip, operator: op })}
            >
              {op.label}
            </Menu.Item>
          ))}
        </Menu.List>
      </Menu>

      {hasValueList ? (
        <EQLFilterValueSelect
          value={chip.value}
          onValueChange={(value) => onChange({ ...chip, value })}
          field={chip.field}
          items={valueOptions ?? []}
          defaultOpen={autoOpen}
        />
      ) : (
        <input
          className={cn(css.segment, css.valueInput)}
          value={chip.value ?? ''}
          title={chip.value ?? undefined}
          onChange={(e) => onChange({ ...chip, value: e.target.value })}
          onBlur={(e) => {
            if (e.target.value === '') onChange({ ...chip, value: null });
          }}
          placeholder="unset"
          autoFocus={autoOpen}
        />
      )}
      {/* TODO: create an Evervault Button variant to handle this */}
      <button
        className={css.remove}
        aria-label="Remove filter"
        onClick={onRemove}
      >
        <X />
      </button>
    </div>
  );
}
